import React from 'react';
import PageLayout from '@/components/PageLayout';
import { AlertTriangle, Info, Shield, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';

const Security: React.FC = () => {
  return (
    <>
    <Helmet>
    <title>Mopaossi | Sécurité</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Politique de confidentialité | Mopaossi"
      description="Politique de confidentialité et de protection des données personnelles."
    >
      <div className="container mx-auto py-12 px-4">        
        <div className="prose max-w-none">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-r">
            <div className="flex items-center">
              <Info className="h-6 w-6 text-blue-500 mr-3" />
              <p className="text-blue-700 font-medium">
                Cette politique de confidentialité a été mise à jour le 1 mai 2025.
              </p>
            </div>
          </div>
          
          <p className="lead">
            Le but de cette politique de confidentialité est d'informer les utilisateurs de notre site des données personnelles que nous recueillons
            ainsi que des informations sur :
          </p>
          
          <ul className="my-4 mx-16 list-decimal">
            <li>Les données personnelles que nous recueillerons</li>
            <li>L'utilisation des données recueillies</li>
            <li>Qui a accès aux données recueillies</li>
            <li>Les droits des utilisateurs du site</li>
            <li>La politique de cookies du site</li>
          </ul>
          
          <p>
            Cette politique de confidentialité s'applique en complément des <a
            href="/terms"
            className="text-blue-600 hover:link-fancy relative after:absolute after:bg-blue-600 after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300"
          >
            Conditions d'utilisation </a> de notre site.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">1. Lois applicables</h2>
          
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6 rounded-r">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-amber-700 font-medium">Conformité réglementaire</p>
                <p className="text-amber-600">
                  Notre politique s'inspire des principes du RGPD européen et des Lignes directrices sur la protection des données à caractère personnel pour l'Afrique, adoptées par l'Union africaine en 2014.
                </p>
              </div>
            </div>
          </div>
          
          <p>
            Bien que notre site soit hébergé aux États-Unis, certains services que nous utilisons s'appuient sur des Content Delivery Networks (CDN) 
            et des infrastructures situées un peu partout dans le monde. Par conséquent, nous nous efforçons de respecter les bonnes pratiques 
            en matière de protection des données personnelles, notamment en nous inspirant des principes du Règlement Général sur la Protection 
            des Données (RGPD) européen et des Lignes directrices sur la protection des données à caractère personnel pour l'Afrique, adoptées par l'Union africaine en 2014, 
            sans toutefois y être légalement soumis dans tous les cas.
          </p>

          <p>Nous nous engageons à ce que les données personnelles soient :</p>
          <ol className="mx-16 list-decimal my-4">
            <li>traitées de manière transparente et équitable ;</li>
            <li>collectées uniquement pour des objectifs précis et légitimes ;</li>
            <li>limitées aux informations strictement nécessaires ;</li>
            <li>maintenues exactes et à jour ;</li>
            <li>conservées uniquement pour la durée nécessaire à la réalisation des objectifs ;</li>
            <li>protégées de manière appropriée contre tout accès non autorisé, perte ou destruction accidentelle.</li>
          </ol>

          <p>Nous ne traiterons vos données que dans les cas suivants :</p>
          <ul className="my-4 mx-16 list-decimal">
            <li>vous avez donné votre consentement explicite ;</li>
            <li>le traitement est nécessaire pour vous fournir nos services ;</li>
            <li>nous avons une obligation légale de le faire ;</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">2. Consentement</h2>
          
          <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6 rounded-r">
            <div className="flex">
              <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-green-700 font-medium">Votre consentement est important</p>
                <p className="text-green-600">
                  En utilisant notre site, vous consentez à notre politique de confidentialité. Vous pouvez retirer votre consentement à tout moment.
                </p>
              </div>
            </div>
          </div>
          
          <p>En utilisant notre site, vous acceptez :</p>
          <ol className="mx-16 list-decimal my-4">
            <li>les conditions énoncées dans la présente politique de confidentialité ;</li>
            <li>la collecte, l'utilisation et la conservation de vos données telles que décrites ici.</li>
          </ol>

          <h2 className="text-2xl font-semibold mt-8 mb-4">3. Données personnelles que nous collectons</h2>

          <h3 className="text-xl font-semibold mt-6 mb-3">3.1. Données collectées automatiquement</h3>
          <p>Lorsque vous visitez notre site, nous collectons automatiquement certaines informations via nos outils d'analyse :</p>
          <ul className="my-4 mx-16 list-decimal">
            <li>Adresse IP</li>
            <li>Localisation géographique approximative</li>
            <li>Liens cliqués par l'utilisateur sur le site</li>
            <li>Pages et contenus consultés</li>
            <li>Type de navigateur, appareil et système d'exploitation</li>
          </ul>

          <p>Ces données sont principalement collectées via :</p>
          <ul className="my-4 mx-16 list-decimal">
            <li>Google Tag Manager</li>
            <li>Google Analytics</li>
            <li>Cloudflare Browser Insights</li>
          </ul>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 my-6 rounded-r">
            <div className="flex">
              <Info className="h-6 w-6 text-purple-500 mr-3 flex-shrink-0" />
              <p className="text-purple-700 text-sm italic">
                Important : Certains de ces services (Google Analytics notamment) peuvent transférer vos données vers les États-Unis.
              </p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-6 mb-3">3.2. Données recueillies de manière non automatique</h3>
          <p>Nous pouvons également collecter les informations suivantes lorsque vous effectuez certaines actions :</p>
          <ol className="my-4 mx-16 list-decimal">
            <li>Prénom et nom</li>
            <li>Date de naissance</li>
            <li>Adresse e-mail</li>
          </ol>

          <p>Ces données sont collectées via :</p>
          <ul className="my-4 mx-16 list-decimal ">
            <li>Formulaires de contact</li>
            <li>Formulaires d'inscription aux évenements</li>
          </ul>
          
          <div className="bg-slate-50 border border-slate-200 shadow-sm p-4 my-6 rounded-lg">
            <div className="flex">
              <Shield className="h-6 w-6 text-slate-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-slate-700 font-medium">Protection des données</p>
                <p className="text-slate-600">
                  Nous ne collectons que les données qui nous aident à atteindre l'objectif énoncé dans cette politique de confidentialité.
                </p>
              </div>
            </div>
          </div>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">4. Comment nous utilisons les données personnelles</h2>
          <p>Les données personnelles recueillies sur notre site seront utilisées uniquement aux fins précisées dans la présente politique ou indiquées
          sur les pages pertinentes de notre site. Nous n'utiliserons pas vos données au-delà de ce que nous divulguerons.</p>
          
          <p>Les données que nous recueillons automatiquement sont utilisées aux fins suivantes :</p>
          <ul className="my-4 mx-16 list-decimal">
            <li>Suivi des performances du site</li>
            <li>Analyse statistique de l'utilisation du site</li>
            <li>Amélioration de la navigation et de l'interface utilisateur</li>
            <li>Détection et prévention des problèmes techniques</li>
          </ul>
          
          <p>Les données que nous recueillons lorsque l'utilisateur exécute certaines fonctions peuvent être utilisées aux fins suivantes :</p>
          <ul className="my-4 mx-16 list-decimal">
            <li>Communication avec vous concernant nos services</li>
            <li>Fourniture des services que vous avez demandés</li>
            <li>Amélioration de l'expérience utilisateur</li>
            <li>Personnalisation du contenu selon vos préférences</li>
          </ul>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">5. Avec qui nous partageons les données personnelles</h2>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">5.1. Employés</h3>
          <p>Nous pouvons divulguer à tout membre de notre organisation les données utilisateur dont ce membre a raisonnablement besoin pour
          réaliser les objectifs énoncés dans la présente politique.</p>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">5.2. Tierces parties</h3>
          <p>Nous ne partageons les données utilisateur à aucun tiers, en dehors des services mentionnés dans la présente politique.</p>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">5.3. Autres divulgations</h3>
          <p>Nous nous engageons à ne pas vendre ou partager vos données avec des tiers, sauf si la loi l'exige.</p>
          
          <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-red-700 font-medium">Divulgation légale</p>
                <p className="text-red-600">
                  Dans certaines circonstances, nous pouvons être légalement tenus de divulguer vos données personnelles, par exemple en réponse à une ordonnance du tribunal ou à une demande d'une autorité gouvernementale.
                </p>
              </div>
            </div>
          </div>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">6. Politique sur les cookies</h2>
          <p>Un cookie est un petit fichier stocké sur le disque dur d'un utilisateur par un site. Il permet de collecter des données concernant les habitudes de navigation et les interactions de l'utilisateur avec le site. Nous utilisons les types de cookies suivants sur notre site :</p>
          
          <ul className="my-4">
            <li><strong>Cookies fonctionnels</strong> - Nous les utilisons pour mémoriser toutes les sélections que vous faites sur notre site afin qu'elles soient sauvegardées pour vos prochaines visites.</li>
            <li><strong>Cookies analytiques</strong> - Nous les utilisons pour nous aider à analyser la façon dont les utilisateurs utilisent le site, afin d'améliorer son fonctionnement.</li>
            <li><strong>Cookies de tiers</strong> - Nous utilisons ces cookies pour améliorer l'expérience utilisateur et analyser l'utilisation du site.</li>
          </ul>
          
          <p>Vous pouvez choisir d'être averti chaque fois qu'un cookie est transmis. Vous pouvez également choisir de désactiver les cookies entièrement dans votre navigateur Internet, mais cela peut diminuer la qualité de votre expérience d'utilisation.</p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">7. Droits de l'utilisateur</h2>
          
          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 my-6 rounded-r">
            <div className="flex">
              <Shield className="h-6 w-6 text-indigo-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-indigo-700 font-medium">Vos droits sont protégés</p>
                <p className="text-indigo-600">
                  Conformément aux principes du RGPD et des Lignes directrices de l'Union africaine, vous disposez de droits spécifiques concernant vos données personnelles.
                </p>
              </div>
            </div>
          </div>
          
          <p>En vertu des principes de protection des données, les utilisateurs ont les droits suivants en tant que personnes concernées :</p>
          <ol className="my-4 mx-16 list-decimal">
            <li>droit d'accès à vos données personnelles</li>
            <li>droit de rectification des données inexactes</li>
            <li>droit à l'effacement (droit à l'oubli)</li>
            <li>droit de restreindre le traitement</li>
            <li>droit à la portabilité des données</li>
            <li>droit d'objection au traitement</li>
          </ol>
          
          <p>Vous trouverez de plus amples informations sur ces droits sur le site de la <a  className="text-blue-600 hover:link-fancy relative after:absolute after:bg-blue-600 after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300" href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">
          Commission Nationale de l'Informatique et des Libertés</a> et dans les <a href="https://au.int/sites/default/files/newsevents/workingdocuments/37470-wd-annex_3_aucprivacyguidelines_201809june_final_fr.pdf" target="_blank" rel="noopener noreferrer">Lignes directrices sur la protection des données à caractère personnel pour l'Afrique</a>.</p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">8. Contact</h2>
          <p>
            Si vous avez des questions concernant cette politique de confidentialité ou si vous souhaitez exercer vos droits,
            veuillez nous contacter par le lien <a
              href="/contact"
              className="text-blue-600 hover:link-fancy relative after:absolute after:bg-blue-600 after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300"
            >
              Contact
            </a>.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 shadow-sm p-4 my-6 rounded-lg">
            <div className="flex">
              <Info className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0" />
              <div>
                <p className="text-blue-700 font-medium">Mise à jour de la politique</p>
                <p className="text-blue-600">
                  Cette politique de confidentialité peut être modifiée occasionnellement. La date de dernière mise à jour est indiquée en haut de cette page.
                </p>
              </div>
            </div>
          </div>
          
          <p>Date de dernière mise à jour : 1 mai 2025</p>
        </div>
      </div>
    </PageLayout>
    </>
  );
};

export default Security;